import { useMemo } from "react";
import { FormHelperText } from "@mui/material";
import { FileUpload } from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import { getErrorMessage } from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import { useNotification } from "@context/NotificationContext";
import type { VesselNominationOutletContext } from "@features/vessel-nomination/add/constants";
import { useUploadDocument } from "@features/vessel-nomination/add/hooks/useUploadDocument";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { LANG_BASE } from "../PreArrivalQuestionnaire";

const FIELD_NAME = "paqDocument";
const URL_FIELD = `${FIELD_NAME}.url`;
const DOCUMENT_NAME_FIELD = `${FIELD_NAME}.documentName`;

const PaqDocument = () => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const { formData, updateFormData } = useOutletContext<VesselNominationOutletContext>();
  const { setFilesForKey, clearFilesForKey, filesByKey } = useUploadStore.getState();
  const files = useMemo(() => filesByKey[FIELD_NAME] || [], [filesByKey]);

  const FILE_UPLOAD_PROFILES = useFileUploadProfiles();
  const uploadConditions = useMemo(() => FILE_UPLOAD_PROFILES.paqDocument, [FILE_UPLOAD_PROFILES]);

  const allowedExtensions = useMemo(
    () =>
      (uploadConditions.format ?? []).map((ext) =>
        ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
      ),
    [uploadConditions.format],
  );

  const certLevelError = getErrorMessage(formData.formState.errors, URL_FIELD);

  const onSuccess = ({
    documentType,
    documentUrl,
    documentName,
  }: {
    documentType: string;
    documentUrl: string;
    documentName: string;
  }) => {
    const { setValue, trigger, clearErrors } = formData;

    setValue(URL_FIELD, documentUrl, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: false,
    });
    setValue(DOCUMENT_NAME_FIELD, documentName, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: false,
    });
    updateFormData(URL_FIELD, documentUrl);
    updateFormData(DOCUMENT_NAME_FIELD, documentName);
    clearErrors(URL_FIELD);

    const entry: UploadEntry = {
      name:
        typeof documentName === "string" && documentName.trim().length > 0
          ? documentName
          : (documentUrl.split("/").pop() ?? documentType),
      url: documentUrl,
      certificateType: documentType,
    };

    setFilesForKey(FIELD_NAME, [entry]);
    trigger(FIELD_NAME);
  };

  const onError = () => {
    notify({
      type: "error",
      title: t("vessel-management.notifications.certificate-upload-error-title"),
      description: t("vessel-management.notifications.certificate-upload-error-description"),
    });

    clearFilesForKey(FIELD_NAME);
  };

  const mutation = useUploadDocument(onSuccess, onError);

  const handleFieldChange = async (value: unknown) => {
    const files = value as File[] | undefined;
    const file = files?.[0];

    if (file) {
      mutation.mutate({ file, documentType: FIELD_NAME });
    }
  };

  const handleReject = () => {
    const typesLabel = uploadConditions.format
      ?.map((type) => type.toUpperCase().replace(".", ""))
      .join(", ");
    formData.setError(URL_FIELD, {
      type: "manual",
      message: t("form-validation.certificate-size-and-type", {
        max: uploadConditions.maxSizeLabel,
        types: typesLabel,
      }),
    });
  };

  const setFiles = (next: UploadEntry[]) => setFilesForKey(FIELD_NAME, next);

  const removeFile = async () => {
    formData.setValue(URL_FIELD, "", {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    formData.setValue(DOCUMENT_NAME_FIELD, "", {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });

    updateFormData(URL_FIELD, "");
    updateFormData(DOCUMENT_NAME_FIELD, "");

    formData.clearErrors(URL_FIELD);
    formData.trigger(FIELD_NAME);
    formData.trigger(FIELD_NAME);
    await formData.trigger(URL_FIELD);

    clearFilesForKey(FIELD_NAME);
  };

  return (
    <FormProvider {...formData}>
      <FormSection title={t(`${LANG_BASE}.sections.paq-document`)} className="grid-cols-1">
        {files[0] ? (
          <SingleFile
            testId={`${FIELD_NAME}-file`}
            file={files[0]}
            fieldName={FIELD_NAME}
            setFiles={() => setFiles([])}
            removeFn={removeFile}
            isLoading={mutation.isPending}
          />
        ) : (
          <div>
            <input type="hidden" {...formData.register(URL_FIELD)} />
            <input type="hidden" {...formData.register(DOCUMENT_NAME_FIELD)} />

            <FileUpload
              testId={`${FIELD_NAME}-upload`}
              onUpload={(files) => handleFieldChange(files)}
              onReject={handleReject}
              maxFiles={1}
              name={FIELD_NAME}
              conditions={uploadConditions}
              allowedExtensions={allowedExtensions}
              uploadDescription={t("upload.additional-documents-upload", {
                extensions: uploadConditions.format.join(", "),
                size: uploadConditions.maxSizeLabel,
              })}
              files={files}
              setFiles={setFiles}
              isError={!!certLevelError}
            />

            {certLevelError && (
              <FormHelperText
                sx={{
                  ml: 0,
                  fontWeight: 500,
                  fontSize: 12,
                  marginY: "4px",
                  height: "20px",
                  color: "error.main",
                }}
              >
                {certLevelError}
              </FormHelperText>
            )}
          </div>
        )}
      </FormSection>
    </FormProvider>
  );
};

export default PaqDocument;
